import { useEffect, useReducer, useRef, useState } from "react";
import { api } from "./api.js";
import { useWsFrames } from "./ws-client.js";

/**
 * Live sync activity state, derived from:
 *   1. `api.listInFlightSyncs()` — initial seed + reseed on WS reconnect.
 *   2. WebSocket `{kind:"sync_progress", jobId, progress}` frames — every
 *      progress update during an active sync, broadcast by sync-enqueue,
 *      sync-worker, force-resync, and dlq-reaper.
 *
 * The earlier SSE feed (`/api/corpus/jobs/stream`) was retired: it ran in
 * the API Lambda's process whose syncBus has been silent ever since the
 * SQS worker took over syncing. Per-page (`currentPage`) and per-attachment
 * (`currentAttachment`) live state went with it — those events are not
 * broadcast over WS today. We surface job-level progress only.
 */
export interface JobActivity {
  jobId: string;
  scopeType: string;
  scopeId: string;
  scopeLabel?: string | null;
  pagesDone: number;
  pagesTotal: number;
  status: "queued" | "running" | "completed" | "partial" | "failed" | "rate_limited";
  startedAt: number;
  finishedAt?: number;
  /** Epoch ms of the most recent progress update (WS frame received OR
   * lastUpdatedAt from the rehydration response). Drives the staleness
   * check in ActivityPanel — without this the panel doesn't notice when
   * a job has gone silent and keeps showing it as "running" indefinitely. */
  lastTickAt: number;
  /** Two-phase progress for force-resync. */
  phase: "purging" | "syncing";
  purgeDone: number;
  purgeTotal: number;
  lastError: string | null;
}

export interface ActivityState {
  jobs: Record<string, JobActivity>;
  /** `${scopeType}:${scopeId}` keys with at least one job in flight. */
  runningKeys: Set<string>;
}

interface ProgressFrame {
  kind: "sync_progress";
  jobId: string;
  progress: {
    jobId: string;
    scopeType: string;
    scopeId: string;
    scopeLabel?: string | null;
    pagesTotal: number;
    pagesDone: number;
    status: JobActivity["status"];
    phase: JobActivity["phase"];
    purgeTotal: number;
    purgeDone: number;
    lastError: string | null;
    startedAt?: string;
  };
}

type Action =
  | { type: "progress"; frame: ProgressFrame }
  | { type: "seedRunning"; keys: string[]; jobs: JobActivity[] };

const TERMINAL: ReadonlySet<JobActivity["status"]> = new Set(["completed", "partial", "failed"]);

function reducer(state: ActivityState, action: Action): ActivityState {
  if (action.type === "seedRunning") {
    const jobs: Record<string, JobActivity> = { ...state.jobs };
    for (const j of action.jobs) jobs[j.jobId] = j;
    return { jobs, runningKeys: new Set(action.keys) };
  }

  const p = action.frame.progress;
  const job: JobActivity = {
    jobId: p.jobId,
    scopeType: p.scopeType,
    scopeId: p.scopeId,
    scopeLabel: p.scopeLabel,
    pagesDone: p.pagesDone,
    pagesTotal: p.pagesTotal,
    status: p.status,
    phase: p.phase,
    purgeDone: p.purgeDone,
    purgeTotal: p.purgeTotal,
    lastError: p.lastError,
    startedAt: p.startedAt ? Date.parse(p.startedAt) : Date.now(),
    finishedAt: TERMINAL.has(p.status) ? Date.now() : undefined,
    lastTickAt: Date.now(),
  };

  const jobs = { ...state.jobs, [job.jobId]: job };
  const runningKeys = new Set(state.runningKeys);
  const key = `${job.scopeType}:${job.scopeId}`;
  if (TERMINAL.has(job.status)) {
    runningKeys.delete(key);
  } else {
    runningKeys.add(key);
  }
  return { jobs, runningKeys };
}

const initial: ActivityState = { jobs: {}, runningKeys: new Set() };

export function useSyncActivity(opts: { initialRunningKeys?: string[] } = {}) {
  const [state, dispatch] = useReducer(reducer, initial);
  // Jobs that were already running when this tab connected. Consumed by
  // CorpusBrowser to re-mount a SyncProgressBanner for each so the user
  // sees in-flight syncs immediately after a page refresh.
  const [rehydratedJobIds, setRehydratedJobIds] = useState<string[]>([]);
  const seeded = useRef(false);

  useEffect(() => {
    if (!seeded.current && opts.initialRunningKeys) {
      dispatch({ type: "seedRunning", keys: opts.initialRunningKeys, jobs: [] });
      seeded.current = true;
    }
  }, [opts.initialRunningKeys]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const { jobs } = await api.listInFlightSyncs();
        if (!alive) return;
        const keys = jobs.map((j) => `${j.scopeType}:${j.scopeId}`);
        const seedJobs: JobActivity[] = jobs.map((j) => ({
          jobId: j.jobId,
          scopeType: j.scopeType,
          scopeId: j.scopeId,
          scopeLabel: j.scopeLabel,
          pagesDone: j.pagesDone,
          pagesTotal: j.pagesTotal,
          status: j.status,
          phase: j.phase,
          purgeDone: j.purgeDone,
          purgeTotal: j.purgeTotal,
          lastError: j.lastError,
          startedAt: j.startedAt ? Date.parse(j.startedAt) : Date.now(),
          lastTickAt: j.lastUpdatedAt ? Date.parse(j.lastUpdatedAt) : Date.now(),
        }));
        dispatch({ type: "seedRunning", keys, jobs: seedJobs });
        setRehydratedJobIds(jobs.map((j) => j.jobId));
      } catch {
        if (!alive) return;
        dispatch({ type: "seedRunning", keys: [], jobs: [] });
        setRehydratedJobIds([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useWsFrames((frame) => {
    if (frame.kind !== "sync_progress") return;
    const f = frame as unknown as ProgressFrame;
    if (!f.progress?.jobId) return;
    dispatch({ type: "progress", frame: f });
  });

  return { ...state, rehydratedJobIds };
}
